import { computed } from 'vue'
import { useQuery, useQueryCache, type EntryKey, type QueryCache } from '@pinia/colada'

import { getDashboardStatus } from '@/api/lytAccountingClient'
import { useAuthStore } from '@/stores/authStore'

/**
 * Dashboard connection/health payload returned by GET /api/v1/dashboard/status.
 * Kept local so the query boundary owns the shape used by DashboardView.
 */
export type DashboardStatus = {
  connected?: boolean
  counts?: Record<string, number>
  lastSync?: Record<string, string>
  company?: {
    companyName?: string
    baseCurrency?: string
    email?: string
  }
}

export type DashboardStatusKeyParts = {
  packageId: string | undefined
  storeId: string | undefined | null
  integrationId: number | null | undefined
}

/** Prefix shared by every dashboard-status cache entry (invalidation target). */
export const DASHBOARD_STATUS_QUERY_ROOT = ['dashboard', 'status'] as const

/**
 * Build a stable, non-secret cache key for dashboard status.
 * Identity is package + store + integration — never the API key.
 */
export function dashboardStatusQueryKey(parts: DashboardStatusKeyParts): EntryKey {
  const packageId = parts.packageId?.trim() || 'anonymous'
  const storeId =
    (typeof parts.storeId === 'string' && parts.storeId.trim()) || 'anonymous'
  const integrationId =
    parts.integrationId === null || parts.integrationId === undefined
      ? 'anonymous'
      : parts.integrationId
  return [...DASHBOARD_STATUS_QUERY_ROOT, packageId, storeId, integrationId]
}

/**
 * Pinia Colada read boundary for the dashboard status endpoint.
 * Keyed by current integration/package/store context so multi-tenant sessions
 * do not share cached health payloads.
 */
export function useDashboardStatusQuery() {
  const auth = useAuthStore()

  // Prefer selectedStore.storeId (store-login flow); fall back to legacy storeID.
  const storeId = computed(
    () => auth.selectedStore?.storeId || auth.storeID || '',
  )

  return useQuery({
    key: () =>
      dashboardStatusQueryKey({
        packageId: auth.selectedPackageId,
        storeId: storeId.value,
        integrationId: auth.currentIntegration?.integrationId ?? null,
      }),
    // Typed client remains the sole HTTP boundary.
    query: async () => (await getDashboardStatus()) as DashboardStatus,
  })
}

/**
 * Invalidate (and refetch active) dashboard status queries after a successful
 * write that can change connection/counts/lastSync (e.g. item stock sync).
 */
export function invalidateDashboardStatus(
  queryCache: QueryCache = useQueryCache(),
): Promise<unknown> {
  return queryCache.invalidateQueries({ key: [...DASHBOARD_STATUS_QUERY_ROOT] })
}
